import { ReactComponent as NoProjects } from '@assets/images/undraw_organizing_projects.svg';
import { EmptyState } from '@components/EmptyState';
import { ExpandableSearchBar } from '@components/ExpandableSearchBar';
import { PageContainer } from '@components/Layout/PageContainer';
import { PageTitle } from '@components/Layout/PageTitle';
import { LoadingScreen } from '@components/LoadingScreen';
import { ResponsiveButton } from '@components/ResponsiveButton';
import { Add, ViewList, ViewModule, Web } from '@mui/icons-material';
import { Button, Grid, Stack, Tab, Tabs, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { deburrSearch } from '@utils/deburrSearch';
import { useGetMe } from '@utils/useGetMe';
import { useResponsive } from '@utils/useResponsive';
import { ProjectFilterInput, useGetProjectsQuery } from 'gql/index';
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Navigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { AddProjectDialog } from '../components/AddProjectDialog/AddProjectDialog';
import { ProjectCard } from '../components/ProjectCard';
import { ProjectListItem } from '../components/ProjectListItem';
import { AddProjectTemplateDialog } from '../components/AddProjectTemplateDialog';
import { ProjectsViewMode, projectsViewModeState } from '../atoms';
import { NoProjectsCustomerView } from './NoProjectsCustomerView';

export const ProjectsPage: React.FC = () => {
  const { formatMessage } = useIntl();
  const { isMobile } = useResponsive();
  const { me, canViewProjectsList, canCreateProject, hasTenantAdminRole } = useGetMe();
  const [selectedTab, setSelectedTab] = useState('mine');
  const [viewMode, setViewMode] = useAtom(projectsViewModeState);

  const filter = useMemo(() => {
    const filters: ProjectFilterInput = {
      and: [
        { isArchived: { eq: selectedTab == 'archived' } },
        { isTemplate: { eq: false } }
      ]
    };

    if (selectedTab === 'mine' && me?.id) {
      filters.and?.push({
        securityGroups: {
          some: {
            members: {
              some: {
                id: {
                  eq: me?.id
                }
              }
            }
          }
        }
      });
    }

    return filters;
  }, [me?.id, selectedTab]);

  const [searchFilter, setSearchFilter] = useState('');

  const { data: projects, isFetching: isFetchingProjects } = useGetProjectsQuery({ filter }, { placeholderData: prev => prev, select: d => d.projects });

  const filteredProjects = projects?.filter(p => deburrSearch(p.name, searchFilter)) ?? [];

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isAddTemplateDialogOpen, setIsAddTemplateDialogOpen] = useState(false);

  const hasSearchResults = filteredProjects.length != 0 && searchFilter != '';
  const isListView = isMobile || viewMode === 'list';

  const handleViewModeChange = (_: React.SyntheticEvent, value: ProjectsViewMode | null) => {
    if (value) {
      setViewMode(value);
    }
  };

  if (projects && canViewProjectsList === false) {
    if (projects.length > 0) {
      return <Navigate to={`${projects[0].id}/home`} />;
    } else {
      return <NoProjectsCustomerView />;
    }
  }
  else if (projects == null) {
    return <LoadingScreen />;
  }

  return <>
    <PageTitle
      title={formatMessage({ id: 'Projects' })}
      icon={<Web />}
      actionButton={canCreateProject && (
        <ResponsiveButton color='primary' variant='contained' icon={<Add />} onClick={() => setIsAddDialogOpen(true)}>
          {formatMessage({ id: 'Create project' })}
        </ResponsiveButton>
      )}
      loading={isFetchingProjects}
      backgroundColor='secondary'
    />

    <AddProjectDialog open={isAddDialogOpen} onClose={() => setIsAddDialogOpen(false)} onAddTemplateRequested={() => setIsAddTemplateDialogOpen(true)} />
    <AddProjectTemplateDialog open={isAddTemplateDialogOpen} onClose={() => setIsAddTemplateDialogOpen(false)} />

    <PageContainer>
      <Stack direction={isMobile ? 'column' : 'row'} justifyContent={'space-between'} alignItems='center' flexWrap='wrap' gap={2} mb={4}>
        <Tabs value={selectedTab} onChange={(_, newValue) => setSelectedTab(newValue)} textColor={'primary'} indicatorColor='primary'>
          <Tab value='mine' label={formatMessage({ id: 'My projects' })} />
          {hasTenantAdminRole && <Tab value='all' label={formatMessage({ id: 'All projects' })} />}
          <Tab value='archived' label={formatMessage({ id: 'Archived' })} />
        </Tabs>

        <Stack direction='row' alignItems='center' gap={2} flex={1} justifyContent={isMobile ? 'flex-start' : 'flex-end'}>
          <ExpandableSearchBar
            fullWidth={isMobile}
            sx={{ width: isMobile ? undefined : '50ch' }}
            value={searchFilter}
            onChange={e => setSearchFilter(e.target.value)}
          />

          {!isMobile && (
            <ToggleButtonGroup
              size='small'
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              aria-label={formatMessage({ id: 'Projects' })}
            >
              <ToggleButton value='cards' aria-label={formatMessage({ id: 'Cards' })}>
                <ViewModule fontSize='small' />
              </ToggleButton>
              <ToggleButton value='list' aria-label={formatMessage({ id: 'List' })}>
                <ViewList fontSize='small' />
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Stack>
      </Stack>

      {filteredProjects.length > 0 && (
        isListView ? (
          <Stack spacing={2}>
            {filteredProjects.map(p => (
              <ProjectListItem key={p.id} project={p} />
            ))}
          </Stack>
        ) : (
          <Grid container spacing={2}>
            {filteredProjects.map(p => (
              <Grid size={{ xs: 12, md: 3 }} key={p.id}>
                <ProjectCard project={p} />
              </Grid>
            ))}
          </Grid>
        )
      )}

      {filteredProjects?.length === 0 && canCreateProject && selectedTab === 'mine' && searchFilter == '' && (
        <Grid size={12} display='flex' alignItems='center' justifyContent='center'>
          <EmptyState
            image={NoProjects}
            title={formatMessage({ id: 'Let\'s create your first project!' })}
            subtitle={formatMessage({ id: 'Choose an option below to begin.' })}
            callToActionComponent={(
              <Stack direction={'row'} spacing={4}>
                <Button variant='contained' startIcon={(<Add />)} onClick={() => setIsAddDialogOpen(true)}>
                  {formatMessage({ id: 'Create project' })}
                </Button>
              </Stack>
            )}
          />
        </Grid>
      )}

      {filteredProjects?.length === 0 && !canCreateProject && selectedTab === 'mine' && searchFilter == '' && (
        <Grid size={12} display='flex' alignItems='center' justifyContent='center'>
          <EmptyState
            image={NoProjects}
            title={formatMessage({ id: 'You have not yet been assigned to any project.' })}
            subtitle={formatMessage({ id: 'Projects will be displayed once an administrator has assigned you to a project.' })}
          />
        </Grid>
      )}

      {!hasSearchResults && searchFilter != '' && (
        <Grid size={12} display='flex' alignItems='center' justifyContent='center'>
          <EmptyState

            title={formatMessage({ id: 'There are no projects matching the selected view and search filter.' })}
          />
        </Grid>
      )}

    </PageContainer>
  </>;
};